/**
 * MobileProductDetail — Food Pulse product detail page.
 *
 * Route: /m/products/:slug
 *
 * Layout (matches the final mockup, top → bottom):
 *   • Green header (Back · FP mark · Home · Bell · Settings)
 *   • Image carousel with dot indicators — pulls `images[]` or falls
 *     back to `image_url`
 *   • Name card (gray) + Category · Sub-category
 *   • Alternate-name pill chips (from `alternate_names` dict)
 *   • Two-column action panel:
 *       - Add to My Lists: Wish / Shopping / Pantry / Donation
 *       - Share or Save:   Share as Deal / Surplus / Donation
 *   • Product Description
 *   • Tabs: Article · Nutrition · Recipes · Tips & Links
 *
 * Content sources per tab:
 *   Article        →  item.article  (admin-authored OR AI-generated)
 *   Nutrition      →  item.nutrition_article  (long form)
 *   Recipes        →  item.recipes[]  (rich objects)
 *   Tips & Links   →  item.tips_article  + item.references[] + item.website_links[]
 *
 * Lists are stored in localStorage under `fp:lists:{kind}` until the
 * server-side `fp_user_lists` collection lands.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Bell, Home as HomeIcon, Settings as SettingsIcon,
  Heart, ListChecks, Bookmark, HandHeart, Share2, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const FP_PURPLE = "#653091";
const FP_GREEN = "#A9D64C";
const FP_GREEN_MID = "#B5DA5E";
const FP_GREEN_BTN = "#93C048";

const TABS = [
  { key: "article",   label: "Article"   },
  { key: "nutrition", label: "Nutrition" },
  { key: "recipes",   label: "Recipes"   },
  { key: "tips",      label: "Tips & Links" },
];

/* ---------- Header ---------- */
function DetailHeader() {
  const nav = useNavigate();
  return (
    <header
      className="sticky top-0 z-30 flex items-center justify-between px-4 py-3"
      style={{ background: `linear-gradient(180deg,${FP_GREEN} 0%,${FP_GREEN_MID} 100%)` }}
    >
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} aria-label="Back" data-testid="product-detail-back"
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/20">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: FP_PURPLE }}>
          <span style={{ color: FP_GREEN, fontFamily: "serif", fontWeight: 700, fontSize: 13 }}>FP</span>
        </div>
        <button onClick={() => nav("/m")} aria-label="Home"><HomeIcon className="w-6 h-6" style={{ color: "#fff" }} /></button>
      </div>
      <div className="flex items-center gap-3">
        <Bell className="w-5 h-5" />
        <SettingsIcon className="w-5 h-5" />
      </div>
    </header>
  );
}

/* ---------- Image carousel ---------- */
function ImageCarousel({ images = [], name }) {
  const [idx, setIdx] = useState(0);
  const list = images.length ? images : [{ url: "" }];
  const cur = list[idx] || list[0];
  return (
    <div className="mx-4 mt-4 rounded-3xl bg-white shadow-sm border border-gray-100 overflow-hidden" data-testid="product-carousel">
      <div className="relative aspect-square w-full flex items-center justify-center bg-white">
        {cur.url ? (
          <img src={cur.url} alt={cur.caption || name} className="max-w-full max-h-full object-contain" />
        ) : (
          <div className="text-gray-400 text-sm">No image</div>
        )}
        {list.length > 1 && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5" data-testid="carousel-dots">
            {list.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Image ${i + 1}`}
                data-testid={`carousel-dot-${i}`}
                className="rounded-full transition-all"
                style={{
                  width: i === idx ? 8 : 6,
                  height: i === idx ? 8 : 6,
                  background: i === idx ? "#333" : "rgba(0,0,0,0.25)",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- List actions (Add-to-Lists / Share) ---------- */
function ActionButton({ Icon, label, onClick, testId, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center justify-center gap-2 rounded-xl py-3 text-white font-semibold shadow-sm active:scale-[0.98] transition-transform"
      style={{ background: FP_GREEN_BTN, opacity: active ? 0.7 : 1 }}
    >
      <Icon className="w-5 h-5" />
      <span>{label}</span>
    </button>
  );
}

function addToLocalList(kind, item) {
  try {
    const key = `fp:lists:${kind}`;
    const cur = JSON.parse(localStorage.getItem(key) || "[]");
    if (cur.find((x) => x.slug === item.slug)) return false;
    cur.unshift({ slug: item.slug, name: item.common_name || item.name, image: item.image_url || (item.images || [])[0]?.url, added_at: new Date().toISOString() });
    localStorage.setItem(key, JSON.stringify(cur.slice(0, 200)));
    return true;
  } catch { return false; }
}

/* ---------- Tab bodies ---------- */

function ArticleBody({ text }) {
  if (!text) {
    return (
      <div className="text-sm text-gray-500 italic" data-testid="tab-article-empty">
        No article yet. The tree admin can generate one from the AI-content panel.
      </div>
    );
  }
  // Simple markdown-ish rendering: paragraph breaks, bold headings (**Foo**),
  // and preserve line breaks. No external MD lib to keep bundle lean.
  return (
    <div className="text-base leading-relaxed whitespace-pre-wrap" data-testid="tab-article-body">
      {text.split(/\n{2,}/).map((para, i) => {
        // Heading detection: line starts with # or is short and TitleCase followed by blank.
        const trimmed = para.trim();
        if (/^#{1,3}\s/.test(trimmed)) {
          return (
            <h3 key={i} className="text-lg font-bold mt-4 mb-1.5">{trimmed.replace(/^#{1,3}\s+/, "")}</h3>
          );
        }
        // Bold-only single-line paragraph → treat as heading.
        if (/^\*\*.+\*\*$/.test(trimmed) && trimmed.length < 90) {
          return <h3 key={i} className="text-lg font-bold mt-4 mb-1.5">{trimmed.replace(/^\*\*|\*\*$/g, "")}</h3>;
        }
        return <p key={i} className="mb-3">{para}</p>;
      })}
    </div>
  );
}

function NutritionBody({ text, structured }) {
  return (
    <div data-testid="tab-nutrition-body">
      <ArticleBody text={text} />
      {structured?.per_100g && (
        <div className="mt-4 rounded-xl border border-gray-200 p-3 bg-white/60">
          <div className="text-sm font-bold mb-2">Per 100 g (quick reference)</div>
          <div className="grid grid-cols-2 gap-y-1 text-xs">
            {Object.entries(structured.per_100g).slice(0, 12).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2 border-b border-gray-100 py-1">
                <span className="opacity-70 capitalize">{k.replace(/_/g, " ")}</span>
                <span className="font-mono">{String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RecipesBody({ recipes = [], name }) {
  if (!recipes.length) {
    return (
      <div className="text-sm text-gray-500 italic" data-testid="tab-recipes-empty">
        No recipes yet. Ask the admin to seed the international-recipes pack.
      </div>
    );
  }
  return (
    <div data-testid="tab-recipes-body">
      <h3 className="text-lg font-bold mb-3">{recipes.length} Detailed {name} Recipe{recipes.length === 1 ? "" : "s"}</h3>
      <ol className="space-y-6">
        {recipes.map((r, i) => (
          <li key={`${r.name}-${i}`} className="text-base leading-relaxed" data-testid={`recipe-item-${i}`}>
            <div className="font-bold">
              {i + 1}. {r.name}
              {r.country && <span className="ml-2 text-xs font-normal text-gray-500">· {r.country}</span>}
            </div>
            {r.description && <p className="text-sm text-gray-700 mt-1 mb-2">{r.description}</p>}
            <div className="mt-1">
              <div className="font-semibold text-sm">Ingredients:</div>
              <ul className="mt-1 whitespace-pre-line text-[15px] leading-relaxed">
                {(Array.isArray(r.ingredients) ? r.ingredients : String(r.ingredients || "").split("\n")).filter(Boolean).map((ing, ii) => (
                  <li key={ii}>{typeof ing === "string" ? ing : `${ing.quantity || ""} ${ing.item || ing.name || ""}`.trim()}</li>
                ))}
              </ul>
            </div>
            <div className="mt-2">
              <div className="font-semibold text-sm">Instructions:</div>
              <div className="mt-1 whitespace-pre-line text-[15px] leading-relaxed">
                {Array.isArray(r.method) ? r.method.map((step, si) => (
                  <div key={si} className="mb-1">{step}</div>
                )) : (r.method || r.instructions || "")}
              </div>
            </div>
            {(r.prep_time || r.cook_time || r.difficulty || r.servings) && (
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {r.prep_time && <span className="rounded-full px-2 py-0.5 bg-gray-100">Prep · {r.prep_time}</span>}
                {r.cook_time && <span className="rounded-full px-2 py-0.5 bg-gray-100">Cook · {r.cook_time}</span>}
                {r.servings && <span className="rounded-full px-2 py-0.5 bg-gray-100">Serves · {r.servings}</span>}
                {r.difficulty && <span className="rounded-full px-2 py-0.5 bg-gray-100">Difficulty · {r.difficulty}</span>}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function TipsBody({ text, references, websiteLinks, videoLinks }) {
  const hasLinks = (references || []).length || (websiteLinks || []).length || (videoLinks || []).length;
  return (
    <div data-testid="tab-tips-body">
      <ArticleBody text={text} />
      {hasLinks && (
        <div className="mt-4 space-y-3">
          {(websiteLinks || []).length > 0 && (
            <div>
              <div className="text-sm font-bold mb-1.5">Website links</div>
              <ul className="space-y-1 text-sm">
                {websiteLinks.map((l, i) => (
                  <li key={i}>
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: FP_PURPLE }}>
                      {l.title || l.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(videoLinks || []).length > 0 && (
            <div>
              <div className="text-sm font-bold mb-1.5">Videos</div>
              <ul className="space-y-1 text-sm">
                {videoLinks.map((l, i) => (
                  <li key={i}>
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: FP_PURPLE }}>
                      {l.title || l.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(references || []).length > 0 && (
            <div>
              <div className="text-sm font-bold mb-1.5">References</div>
              <ul className="space-y-1 text-xs opacity-80">
                {references.map((r, i) => (
                  <li key={i}>
                    {r.url ? (
                      <a href={r.url} target="_blank" rel="noopener noreferrer" className="underline">{r.title || r.url}</a>
                    ) : (
                      <span>{r.title}</span>
                    )}
                    {r.publisher && <span className="opacity-70"> — {r.publisher}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Main screen ---------- */

export default function MobileProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("article");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/food-items/${slug}`)
      .then((r) => { if (!cancelled) setItem(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load product."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (item?.common_name) document.title = `${item.common_name} — Food Pulse`;
  }, [item]);

  const altNames = useMemo(() => {
    if (!item) return [];
    const dict = item.alternate_names || {};
    const values = Object.values(dict).filter((v) => typeof v === "string" && v.trim());
    // Deduplicate + strip the common_name itself.
    const seen = new Set([(item.common_name || "").toLowerCase()]);
    const out = [];
    for (const v of values) {
      const k = v.trim().toLowerCase();
      if (!seen.has(k)) { seen.add(k); out.push(v.trim()); }
    }
    return out.slice(0, 8);
  }, [item]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="product-detail-loading">
        <DetailHeader />
        <div className="flex-1 flex items-center justify-center opacity-60 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
        </div>
      </div>
    );
  }
  if (err || !item) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="product-detail-error">
        <DetailHeader />
        <div className="p-6 text-center">
          <div className="text-base font-bold mb-2">Product not found</div>
          <div className="text-sm text-gray-500">{err || `We couldn't find "${slug}".`}</div>
          <button onClick={() => nav("/m/products")} className="mt-4 px-4 py-2 rounded-lg text-sm text-white" style={{ background: FP_PURPLE }}>
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  const categoryChips = [];
  // Match mockup: "Perishable · Leafy Greens" — treat category+sub-category as two chips.
  const perishTag = ((item.tags || []).find((t) => /perishable/i.test(t)) || (item.storage?.perishable ? "Perishable" : ""));
  if (perishTag) categoryChips.push(perishTag);
  const subLabel = (item.sub_category || item.category || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  if (subLabel) categoryChips.push(subLabel);

  const bg = "linear-gradient(180deg,#DFF0AF 0%,#F3F9DA 40%,#FFFFFF 100%)";

  return (
    <div className="min-h-screen" style={{ background: bg }} data-testid="mobile-product-detail">
      <DetailHeader />

      <ImageCarousel images={item.images || []} name={item.common_name} />

      {/* Name card */}
      <div className="mx-4 mt-4 rounded-2xl px-4 py-3" style={{ background: "#EFEFEF" }} data-testid="product-name-card">
        <h1 className="text-2xl font-extrabold leading-tight" data-testid="product-name">{item.common_name}</h1>
        {categoryChips.length > 0 && (
          <div className="mt-1 text-base opacity-80" data-testid="product-category-line">
            {categoryChips.join("  ·  ")}
          </div>
        )}
      </div>

      {/* Alt-name chips */}
      {altNames.length > 0 && (
        <div className="mx-4 mt-4 flex gap-3 flex-wrap" data-testid="product-alt-chips">
          {altNames.map((n, i) => (
            <span key={i} className="rounded-full px-5 py-2 text-white text-base" style={{ background: FP_GREEN_BTN }}>
              {n}
            </span>
          ))}
        </div>
      )}

      {/* Add / Share panel */}
      <div className="mx-4 mt-5 grid grid-cols-2 gap-4">
        {/* Left column — Add to My Lists */}
        <div>
          <div className="text-lg font-bold mb-2">Add to My Lists</div>
          <div className="space-y-2">
            <ActionButton Icon={Heart}      label="Wish List"     testId="add-to-wish"     onClick={() => { const ok = addToLocalList("wishlist", item); toast[ok ? "success" : "info"](ok ? "Added to Wish List" : "Already in Wish List"); }} />
            <ActionButton Icon={ListChecks} label="Shopping List" testId="add-to-shopping" onClick={() => { const ok = addToLocalList("shopping", item); toast[ok ? "success" : "info"](ok ? "Added to Shopping List" : "Already in Shopping List"); }} />
            <ActionButton Icon={Bookmark}   label="Pantry List"   testId="add-to-pantry"   onClick={() => { const ok = addToLocalList("pantry", item);   toast[ok ? "success" : "info"](ok ? "Added to Pantry List"   : "Already in Pantry List"); }} />
            <ActionButton Icon={HandHeart}  label="Donation List" testId="add-to-donation" onClick={() => { const ok = addToLocalList("donation", item); toast[ok ? "success" : "info"](ok ? "Added to Donation List" : "Already in Donation List"); }} />
          </div>
        </div>
        {/* Right column — Share */}
        <div>
          <div className="text-lg font-bold mb-2">Share or Save This Item</div>
          <div className="space-y-2">
            <ActionButton Icon={Share2} label="Share as Deal"     testId="share-as-deal"     onClick={() => nav(`/m/add-deal?product_slug=${item.slug}&kind=offer`)} />
            <ActionButton Icon={Share2} label="Share as Surplus"  testId="share-as-surplus"  onClick={() => nav(`/m/add-deal?product_slug=${item.slug}&kind=surplus`)} />
            <ActionButton Icon={Share2} label="Share as Donation" testId="share-as-donation" onClick={() => nav(`/m/add-deal?product_slug=${item.slug}&kind=donation`)} />
          </div>
        </div>
      </div>

      {/* Product Description */}
      {item.description && (
        <div className="mx-4 mt-5" data-testid="product-description">
          <div className="text-lg font-bold">Product Description :</div>
          <p className="text-base leading-relaxed mt-1 whitespace-pre-line">{item.description}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="mx-4 mt-5 flex gap-3 overflow-x-auto pb-2" data-testid="product-tabs">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`product-tab-${t.key}`}
              className="px-5 py-2 rounded-lg border font-semibold transition-colors flex-none"
              style={{
                background: active ? FP_GREEN_BTN : "#fff",
                color: active ? "#fff" : "#111",
                borderColor: active ? FP_GREEN_BTN : "#d1d5db",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab body */}
      <div className="mx-4 mt-2 pb-24 min-h-[40vh]" data-testid={`product-tab-body-${tab}`}>
        {tab === "article"   && <ArticleBody   text={item.article} />}
        {tab === "nutrition" && <NutritionBody text={item.nutrition_article} structured={item.nutrition} />}
        {tab === "recipes"   && <RecipesBody   recipes={item.recipes || []} name={item.common_name} />}
        {tab === "tips"      && <TipsBody
            text={item.tips_article}
            references={item.references}
            websiteLinks={item.website_links}
            videoLinks={item.video_links}
          />}
      </div>
    </div>
  );
}
